// Viviplu Limited privacy policy, generated from the published Word document
// ("Viviplu Privacy Policy (PS 13.05.25).docx", linked from viviplu.co.uk). Keep the wording as published.

export type PolicyBlock =
  | { type: 'p'; text: string }
  | { type: 'list'; items: string[] }
  | {
      type: 'table';
      groups: {
        group: string;
        rows: { situation: string; information: string | null; purpose: string[] }[];
      }[];
    };

export interface PolicySection {
  heading: string;
  blocks: PolicyBlock[];
}

export const PRIVACY_POLICY_UPDATED = "6 March 2025";

export const PRIVACY_POLICY: PolicySection[] = [
  {
    "heading": "Our details",
    "blocks": [
      {
        "type": "p",
        "text": "We are the data controller with conduct of your personal information."
      },
      {
        "type": "p",
        "text": "Our company name is Viviplu Limited and our company number is 16102436."
      },
      {
        "type": "p",
        "text": "You can contact us by post to: Viviplu Limited, 1 London Road, Southampton, SO15 2AE."
      },
      {
        "type": "p",
        "text": "You can also contact us by email to: info@viviplu.co.uk"
      }
    ]
  },
  {
    "heading": "How we use your personal information",
    "blocks": [
      {
        "type": "p",
        "text": "The following table explains how we use your personal information in our business. Different sections apply depending on your relationship to us. Please read each section carefully as more than one section may apply to you."
      },
      {
        "type": "table",
        "groups": [
          {
            "group": "Visitors to our website",
            "rows": [
              {
                "situation": "We monitor how users interact with our website through the use of cookies.",
                "information": "IP address, approximate location.",
                "purpose": [
                  "We set the cookies and process any personal data captured by them based on your consent."
                ]
              },
              {
                "situation": "You send us an enquiry regarding our products/services.",
                "information": "Name, contact information, details of your enquiry.",
                "purpose": [
                  "We have a legitimate interest in responding to your enquiry.",
                  "We process your information to carry out pre-contractual steps relating to a potential contract between us.",
                  "We have a legitimate interest in keeping a record of your request as well as our response."
                ]
              }
            ]
          },
          {
            "group": "Organisational customers (including their staff), suppliers (including their staff)",
            "rows": [
              {
                "situation": "We send you marketing communications regarding our products and services.",
                "information": "Name, contact information, marketing preferences.",
                "purpose": [
                  "We have a legitimate interest in sending you updates about our goods/services.",
                  "You can opt-out of these communications by following the link in each message or by contacting us directly."
                ]
              },
              {
                "situation": "Your organisation purchases goods or services from us.",
                "information": "Name, contact information, job title, payment details, details of the goods/services purchased.",
                "purpose": [
                  "We have a legitimate interest in processing your personal information for the purpose of managing the contractual relationship between your organisation and us.",
                  "We have a legitimate interest in keeping a record of the contract between your organisation and us for the administration of our business and to address any disputes which may arise between your organisation and us."
                ]
              },
              {
                "situation": "You or your organisation supplies goods or services to us.",
                "information": "Name, contact information, job title, payment details, details of the goods/services supplied.",
                "purpose": [
                  "If the contract is with you, the processing is necessary for us to perform our obligations under the contract.",
                  "If the contract is with your organisation then we have a legitimate interest in processing your personal information for the purpose of managing the contractual relationship between your organisation and us.",
                  "We have a legitimate interest in keeping a record of the contract between us (or your organisation and us) for the administration of our business and to address any disputes which may arise between us."
                ]
              }
            ]
          },
          {
            "group": "Users of the Ask Joan app",
            "rows": [
              {
                "situation": "You use the Ask Joan app under a scheme arranged by another organisation (e.g. a local authority, charity or the police).",
                "information": null,
                "purpose": [
                  "The data controller is the organisation which operates the scheme you are part of. We are acting as a data processor on behalf of that organisation.",
                  "For information about how your personal data is processed, please read the organisation’s Privacy Policy. If you have any questions then please send these to the organisation running the scheme."
                ]
              },
              {
                "situation": "You use the Ask Joan app under a direct arrangement with us (i.e. you are not part of a scheme run by a local authority, charity, the police, etc.)",
                "information": "Name, contact information, details of the app store account you used to install the app, payment records.",
                "purpose": [
                  "The processing is necessary for us to perform our obligations under the contract.",
                  "We have a legitimate interest in keeping records to help us operate our business, including to manage complaints and disputes."
                ]
              }
            ]
          },
          {
            "group": "Users of our support services",
            "rows": [
              {
                "situation": "You contact us for support with the Ask Joan app",
                "information": "Name, contact information.",
                "purpose": [
                  "We have a legitimate interest in assisting you with setting up the Ask Joan app.",
                  "We also have a legitimate interest in keeping records of requests for support so that we can manage the demand on our services and identify trends. Where possible, we will anonymise this data."
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "heading": "How long we keep your information for",
    "blocks": [
      {
        "type": "p",
        "text": "We only keep your information for so long as is reasonably necessary. Generally speaking, we keep your personal information for the following periods of time:"
      },
      {
        "type": "list",
        "items": [
          "General enquiries – three years from when we resolve your enquiry, unless it results in us entering into a contract with you or your organisation, in which case the below time period applies instead.",
          "Customers and staff of customers - the duration of the customer contract plus 7 years.",
          "Suppliers and staff of suppliers - the duration of the supplier contract plus 7 years."
        ]
      }
    ]
  },
  {
    "heading": "Who is your information shared with?",
    "blocks": [
      {
        "type": "p",
        "text": "We share your personal information with third parties only where we are required to do so to comply with the law, to protect our rights, to perform our contractual obligations or to efficiently operate our business. In order to achieve these purposes, we share your data with the following people or group of people:"
      },
      {
        "type": "list",
        "items": [
          "If you are an employee or representative of a customer or supplier of ours then we may share your information with your employer and colleagues for the purpose of managing the business relationship between your organisation and us.",
          "Our outsourced service providers. These organisations have strict contractual obligations to handle your information in accordance with data protection law and to keep it confidential at all times.",
          "Our professional advisers (including accountants and lawyers). These people are subject to professional duties of confidentiality.",
          "Potential purchasers of our business may be given access to redacted information about our customers, users, suppliers and their staff/representatives. Before we share this information the potential purchaser would need to sign a non-disclosure agreement which protects your personal information."
        ]
      },
      {
        "type": "p",
        "text": "We do not transfer your personal information outside of the UK."
      }
    ]
  },
  {
    "heading": "Your rights",
    "blocks": [
      {
        "type": "p",
        "text": "Under UK data protection law you have the following rights:"
      },
      {
        "type": "list",
        "items": [
          "The right to be informed about what we do with your information. This policy provides you with this information.",
          "If we are processing your data on the basis of your consent then you have the right to withdraw that consent at any time.",
          "The right to access a copy of your information which we hold. This is called a ‘data subject access request’. Additional details on how to exercise this right are set out in the ‘Access to Information’ section below.",
          "The right to prevent us processing your information for direct marketing purposes. We will usually inform you (before collecting your data) if we intend to use your data for such purposes or if we intend to disclose your information to any third party for such purposes. You can exercise your right to prevent such processing by checking certain boxes on the forms we use to collect your data or by contacting us using the details set out at the start of this policy.",
          "The right to object to decisions being made about you by automated means. We will inform you if your information is subject to automated processing.",
          "The right to object to us processing your personal information in certain other situations.",
          "The right, in certain circumstances, to have your information rectified, blocked, erased or destroyed if it is inaccurate.",
          "The right, in certain circumstances, to claim compensation for damages caused by us breaching data protection law.",
          "The right, in certain circumstances, to request that we erase, rectify, cease processing and/or delete your information."
        ]
      },
      {
        "type": "p",
        "text": "You have the general right to complain to us in the first instance if you are not happy with how we are processing your personal information. If you are not satisfied by our response then you can complain to the Information Commissioner’s Office (ICO) via www.ico.org.uk."
      }
    ]
  },
  {
    "heading": "Access to information",
    "blocks": [
      {
        "type": "p",
        "text": "Under UK data protection law you can exercise your right of access by making a written request to receive copies of some of the information we hold on you. We may need to request proof of your identity, or proof of your authority (if making the request on behalf of someone else), before we can supply the information to you. Requests should be sent to us using the contact details set out at the start of this policy."
      },
      {
        "type": "p",
        "text": "You do not need to pay a fee to exercise this right unless you are requesting copies of documents you already possess, in which case we may charge our reasonable administrative costs. We are also allowed to charge you for our reasonable administrative costs in collating and providing you with details of the requested information which we hold about you if your request is clearly unfounded or excessive. In very limited circumstances, we are entitled to refuse to comply with your request."
      },
      {
        "type": "p",
        "text": "In certain circumstances, you are entitled to receive the information in a structured, commonly used and machine-readable form."
      }
    ]
  },
  {
    "heading": "Changes to our privacy policy",
    "blocks": [
      {
        "type": "p",
        "text": "This policy was last updated on 6 March 2025. Any changes we may make to our privacy policy will be uploaded to our website and if the change is significant then we will tell you about it by email or (if you are an app user) via a notification within the Ask Joan app."
      }
    ]
  }
];
